/**
 * Workspace Transformation Utilities
 *
 * This file contains utility functions for transforming workspace data
 * into various formats for display in the UI. It provides:
 * - Date formatting utilities (relative time formatting)
 * - User image fetching and caching
 * - Workspace data transformation for different UI components
 * - Workspace grouping and sorting
 * - Data room item sorting and counting
 *
 * These utilities handle the conversion of raw API workspace data into
 * formats suitable for landing pages, dropdowns, and detailed views.
 */

// ============= Imports =============

// APIs
import {
  DEFAULT_AVATAR_FALLBACK,
  getUserImage,
} from '@/apis/core/user/get/getUserImage';
import { postUser, PostUserResponse } from '@/apis/core/user/post/postUser';
import {
  UserWorkspace,
  UserWorkspaceResponse,
} from '@/apis/core/workspace/get/getUserWorkspace';
import type { AskSession } from '@/apis/ask/askHistory/types';

// Contexts
import { AuthContextProps } from '@/contexts/AuthContext';

// ============= Types =============

/**
 * Options for date formatting.
 */
export interface FormatLastUsedOptions {
  /** Whether to use compact format (e.g., "1m ago" vs "1 minute ago") */
  compact?: boolean;
}

/**
 * Workspace item structure for landing page display.
 */
export interface WorkspaceItem {
  /** Workspace ID */
  id: string;
  /** Workspace title */
  title: string;
  /** Formatted "last used" time string */
  lastUsed: string;
  /** Optional array of member avatar URLs */
  memberAvatars?: string[];
  /** Optional: member user IDs, same order as memberAvatars */
  memberUserIds?: string[];
  /** Optional: member admin/owner flags, same order as memberAvatars */
  memberIsAdmin?: boolean[];
  /** Optional: member online status, same order as memberAvatars */
  memberIsOnline?: boolean[];
  /** Optional: member display names, same order as memberAvatars */
  memberUserNames?: string[];
  /** Workspace type (Private, Collaborative, Standard) */
  workspaceType?: string;
  /** Number of documents in the workspace data room */
  documentCount?: number;
  /** Number of ask sessions in the workspace */
  askSessionCount?: number;
  /** Number of documents linked to ask sessions */
  documentsLinkedToAsksCount?: number;
  /** Client name when available */
  clientName?: string | null;
  /** Matter name when available */
  matterName?: string | null;
}

/**
 * Workspace group structure for landing page.
 */
export interface WorkspaceGroup {
  /** Group title (e.g., "Private", "Collaborative") */
  title: string;
  /** Array of workspace items in this group */
  items: WorkspaceItem[];
}

/**
 * User metadata structure for transformed workspaces.
 * Contains user information including image, admin status, and online status.
 */
export interface UserMetadata {
  /** User profile image URL */
  image: string;
  /** Whether the user is an admin in the workspace */
  isOwner: boolean;
  /** Whether the user is currently online */
  isOnline: boolean;
  /** User ID */
  userId: string;
}

/**
 * Transformed workspace structure with enhanced data room and user information.
 * Extends the base UserWorkspace with sorted data and user metadata.
 */
export interface TransformedUserWorkspace extends Omit<
  UserWorkspace,
  'dataRoom' | 'workspaceUsers'
> {
  /** Enhanced data room structure with sorted items and counts */
  dataRoom: {
    workspaceDataRoomFiles: {
      items: unknown[];
      count: number;
    };
    workspaceDataRoomCollections: {
      items: unknown[];
      count: number;
    };
    workspaceCentralDataStores: {
      items: unknown[];
      count: number;
    };
  };
  /** Enhanced workspace users structure with images and metadata */
  workspaceUsers: {
    /** Map of user IDs to admin status */
    users: Record<string, boolean>;
    /** Map of user IDs to image URLs */
    userImages: Record<string, string>;
    /** Map of user IDs to user metadata */
    userMetadata: Record<string, UserMetadata>;
    /** Map of user IDs to full user data */
    userData: Record<string, PostUserResponse>;
  };
}

// ============= Constants =============

/**
 * Workspace type constants.
 */
const WORKSPACE_TYPE_PRIVATE = 'Private' as const;
const WORKSPACE_TYPE_COLLABORATIVE = 'Collaborative' as const;
const WORKSPACE_TYPE_STANDARD = 'Standard' as const;

/**
 * Time thresholds for relative time formatting.
 */
const THRESHOLD_SECONDS = 60;
const THRESHOLD_MINUTES = 60;
const THRESHOLD_HOURS = 24;
const THRESHOLD_DAYS_WEEK = 7;
const THRESHOLD_DAYS_MONTH = 30;
const THRESHOLD_DAYS_YEAR = 365;

/**
 * User image dimension for avatars.
 */
const USER_IMAGE_DIMENSION = '48';

/**
 * Maximum number of member avatars to display per workspace.
 */
const MAX_MEMBER_AVATARS = 5;

/**
 * Sort keys to try when sorting unknown array items.
 * Ordered by priority (most common first).
 */
const SORT_KEYS = [
  'lastUpdate',
  'createdOn',
  'updatedOn',
  'modifiedOn',
  'name',
  'title',
] as const;

/**
 * Group titles for workspace types.
 */
const GROUP_TITLE_PRIVATE = 'Private';
const GROUP_TITLE_COLLABORATIVE = 'Collaborative';
const GROUP_TITLE_STANDARD = 'Standard';

// ============= Utilities =============

/**
 * Formats a date into a human-readable "time ago" format.
 * Supports both compact format (e.g., "1m ago") and full format (e.g., "1 minute ago").
 *
 * @param dateInput - Date string or Date object to format
 * @param options - Formatting options
 * @param options.compact - Whether to use compact format (default: true)
 * @returns Formatted time string (e.g., "2h ago", "Yesterday", "3 days ago")
 */
export const formatLastUsed = (
  dateInput: string | Date,
  options?: FormatLastUsedOptions,
): string => {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();

  // Handle future dates
  if (diffInMs < 0) {
    return 'Just now';
  }

  // Calculate time differences
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / THRESHOLD_SECONDS);
  const diffInHours = Math.floor(diffInMinutes / THRESHOLD_MINUTES);
  const diffInDays = Math.floor(diffInHours / THRESHOLD_HOURS);
  const diffInWeeks = Math.floor(diffInDays / THRESHOLD_DAYS_WEEK);
  const diffInMonths = Math.floor(diffInDays / THRESHOLD_DAYS_MONTH);
  const diffInYears = Math.floor(diffInDays / THRESHOLD_DAYS_YEAR);

  const compact = options?.compact ?? true;

  // Format based on time difference
  if (diffInSeconds < THRESHOLD_SECONDS) {
    return 'Just now';
  } else if (diffInMinutes < THRESHOLD_MINUTES) {
    if (compact) {
      return `${diffInMinutes}m ago`;
    }
    return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
  } else if (diffInHours < THRESHOLD_HOURS) {
    if (compact) {
      return `${diffInHours}h ago`;
    }
    return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
  } else if (diffInDays === 1) {
    return 'Yesterday';
  } else if (diffInDays < THRESHOLD_DAYS_WEEK) {
    if (compact) {
      return `${diffInDays}d ago`;
    }
    return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
  } else if (diffInDays < THRESHOLD_DAYS_MONTH) {
    if (compact) {
      return `${diffInWeeks}w ago`;
    }
    return `${diffInWeeks} ${diffInWeeks === 1 ? 'week' : 'weeks'} ago`;
  } else if (diffInDays < THRESHOLD_DAYS_YEAR) {
    if (compact) {
      return `${diffInMonths}mo ago`;
    }
    return `${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'} ago`;
  } else {
    if (compact) {
      return `${diffInYears}y ago`;
    }
    return date.toLocaleDateString();
  }
};

/**
 * Fetches user images for multiple user IDs in parallel.
 * Handles errors gracefully and returns a map of userId to imageUrl.
 *
 * @param userIds - Array of user IDs to fetch images for
 * @param getAccessToken - Function to get access token for API calls
 * @returns Promise resolving to a map of userId to imageUrl
 */
const fetchUserImages = async (
  userIds: string[],
  getAccessToken: AuthContextProps['getAccessToken'],
): Promise<Record<string, string>> => {
  const imageMap: Record<string, string> = {};

  // Fetch images in parallel; API returns fallback on failure, cache avoids repeat calls
  const imagePromises = userIds.map(async (userId) => {
    try {
      const imageUrl = await getUserImage({
        getAccessToken: getAccessToken,
        userId,
        dimension: USER_IMAGE_DIMENSION,
      });
      return { userId, imageUrl };
    } catch (error) {
      console.error(`Failed to fetch image for user ${userId}:`, error);
      return { userId, imageUrl: DEFAULT_AVATAR_FALLBACK };
    }
  });

  const results = await Promise.all(imagePromises);
  results.forEach(({ userId, imageUrl }) => {
    imageMap[userId] = imageUrl;
  });

  return imageMap;
};

/**
 * Fetches full user data for multiple user IDs in a single API call.
 * Uses postUser API to get complete user information for all users at once.
 * Handles errors gracefully and returns a map of userId to PostUserResponse.
 *
 * @param userIds - Array of user IDs to fetch data for
 * @param getAccessToken - Function to get access token for API calls
 * @returns Promise resolving to a map of userId to PostUserResponse
 */
const fetchUserData = async (
  userIds: string[],
  getAccessToken: AuthContextProps['getAccessToken'],
): Promise<Record<string, PostUserResponse>> => {
  const userDataMap: Record<string, PostUserResponse> = {};

  // Return empty map if no user IDs provided
  if (userIds.length === 0) {
    return userDataMap;
  }

  try {
    // Fetch all user data in a single API call
    const users = await postUser({
      getAccessToken: getAccessToken,
      body: userIds,
    });

    // Map the results by user ID
    if (Array.isArray(users)) {
      users.forEach((user) => {
        if (user && user.id) {
          userDataMap[user.id] = user;
        }
      });
    }
  } catch (error) {
    console.error('Failed to fetch user data:', error);
    // Return empty map on error - individual workspaces will handle missing data gracefully
  }

  return userDataMap;
};

/**
 * Sorts an array of unknown items by common date/name properties.
 * Tries to sort by: lastUpdate, createdOn, updatedOn, modifiedOn, name, title (in that order).
 * Handles date strings and regular strings appropriately.
 *
 * @param arr - Array of unknown items to sort
 * @returns Sorted array (newest first for dates, alphabetical for strings)
 */
const sortUnknownArray = (arr: unknown[]): unknown[] => {
  if (!Array.isArray(arr) || arr.length === 0) {
    return arr;
  }

  // Try to find a sortable property from the first item
  const firstItem = arr[0];
  if (typeof firstItem !== 'object' || firstItem === null) {
    return arr;
  }

  const item = firstItem as Record<string, unknown>;

  // Find the first available sort key
  const sortKey = SORT_KEYS.find(
    (key) => key in item && item[key] !== null && item[key] !== undefined,
  );

  if (!sortKey) {
    return arr; // No sortable property found, return as-is
  }

  // Sort the array
  return [...arr].sort((a, b) => {
    const aItem = a as Record<string, unknown>;
    const bItem = b as Record<string, unknown>;
    const aValue = aItem[sortKey];
    const bValue = bItem[sortKey];

    // Handle date strings
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const aDate = new Date(aValue).getTime();
      const bDate = new Date(bValue).getTime();
      if (!isNaN(aDate) && !isNaN(bDate)) {
        return bDate - aDate; // Descending order (newest first)
      }
    }

    // Handle string comparison
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return aValue.localeCompare(bValue);
    }

    return 0;
  });
};

/**
 * Sorts workspaces by last update time (newest first).
 *
 * @param a - First workspace
 * @param b - Second workspace
 * @returns Comparison result for sorting
 */
const sortWorkspacesByLastUpdate = (
  a: UserWorkspace,
  b: UserWorkspace,
): number => {
  return new Date(b.lastUpdate).getTime() - new Date(a.lastUpdate).getTime();
};

/**
 * Extracts member avatars for a workspace from user images.
 * Limits the number of avatars to MAX_MEMBER_AVATARS.
 *
 * @param workspaceUsers - Workspace users object
 * @param userImages - Map of userId to imageUrl
 * @returns Array of avatar URLs (max MAX_MEMBER_AVATARS)
 */
const getMemberAvatars = (
  workspaceUsers: Record<string, boolean>,
  userImages: Record<string, string>,
): string[] => {
  return Object.keys(workspaceUsers)
    .map((userId) => userImages[userId])
    .filter((avatar): avatar is string => avatar !== undefined)
    .slice(0, MAX_MEMBER_AVATARS);
};

/**
 * Extracts member metadata (userIds, isAdmin) in the same order as getMemberAvatars.
 */
const getMemberMetadata = (
  workspaceUsers: Record<string, boolean>,
  userImages: Record<string, string>,
): { userIds: string[]; isAdmin: boolean[] } => {
  const userIds = Object.keys(workspaceUsers)
    .filter((userId) => userImages[userId] !== undefined)
    .slice(0, MAX_MEMBER_AVATARS);
  const isAdmin = userIds.map((userId) => Boolean(workspaceUsers[userId]));
  return { userIds, isAdmin };
};

/**
 * Transforms a single workspace to a workspace item.
 *
 * @param workspace - Workspace to transform
 * @param getAccessToken - Function to get access token
 * @returns Transformed workspace item
 */
export const transformWorkspaceToItem = async (
  workspace: UserWorkspace,
  getAccessToken: AuthContextProps['getAccessToken'],
): Promise<WorkspaceItem> => {
  // Note: This function fetches user images but doesn't use them
  // Consider removing if not needed, or using them if avatars are required
  await fetchUserImages([workspace.creatorId], getAccessToken);
  return {
    id: workspace.id,
    title: workspace.title,
    lastUsed: formatLastUsed(workspace.lastUpdate, { compact: false }),
  };
};

/**
 * Creates a workspace group from an array of workspaces.
 *
 * @param workspaces - Array of workspaces to group
 * @param title - Group title
 * @param userImages - Map of userId to imageUrl (optional, for member avatars)
 * @returns Workspace group or null if no workspaces
 */
const createWorkspaceGroup = (
  workspaces: UserWorkspace[],
  title: string,
  userImages?: Record<string, string>,
): WorkspaceGroup | null => {
  if (workspaces.length === 0) {
    return null;
  }

  return {
    title,
    items: workspaces.sort(sortWorkspacesByLastUpdate).map((ws) => {
      const documentCount = Array.isArray(ws.dataRoom?.workspaceDataRoomFiles)
        ? ws.dataRoom.workspaceDataRoomFiles.length
        : 0;
      const askSessions = ws.askSessions ?? [];
      const askSessionCount = askSessions.length;
      const documentsLinkedToAsksCount = (askSessions as AskSession[]).reduce(
        (sum, session) => sum + (session.files?.length ?? 0),
        0,
      );

      const item: WorkspaceItem = {
        id: ws.id,
        title: ws.title,
        lastUsed: formatLastUsed(ws.lastUpdate, { compact: false }),
        documentCount,
        askSessionCount,
        documentsLinkedToAsksCount,
        clientName: ws.clientName ?? undefined,
        matterName: ws.matterName ?? undefined,
      };

      // Add member avatars and metadata if user images are provided
      if (userImages) {
        const memberAvatars = getMemberAvatars(ws.workspaceUsers, userImages);
        if (memberAvatars.length > 0) {
          item.memberAvatars = memberAvatars;
          const { userIds, isAdmin } = getMemberMetadata(
            ws.workspaceUsers,
            userImages,
          );
          item.memberUserIds = userIds;
          item.memberIsAdmin = isAdmin;
          // Same order as memberUserIds: one boolean per user (updated by presence in WorkspaceContext)
          item.memberIsOnline = userIds.map(() => false);
        }
      }

      return item;
    }),
  };
};

/**
 * Collects all unique user IDs from an array of workspaces.
 *
 * @param workspaces - Array of workspaces
 * @returns Set of unique user IDs
 */
const collectUserIds = (workspaces: UserWorkspace[]): Set<string> => {
  const userIdsSet = new Set<string>();
  workspaces.forEach((ws) => {
    Object.keys(ws.workspaceUsers).forEach((userId) => {
      userIdsSet.add(userId);
    });
  });
  return userIdsSet;
};

// ============= Main Transformation Functions =============

/**
 * Transforms workspace data into grouped format for display.
 * Groups workspaces by type (Private, Collaborative, Standard).
 * Fetches user images for collaborative and standard workspaces.
 *
 * @param workspaces - Raw workspace data from API
 * @param getAccessToken - Function to get access token for API calls
 * @returns Promise resolving to array of workspace groups
 */
export const transformWorkspacesToItems = async (
  workspaces: UserWorkspaceResponse,
  getAccessToken: AuthContextProps['getAccessToken'],
): Promise<WorkspaceGroup[]> => {
  // Group workspaces by type
  const privateWorkspaces = workspaces.filter(
    (ws) => ws.workspaceType === WORKSPACE_TYPE_PRIVATE,
  );
  const collaborativeWorkspaces = workspaces.filter(
    (ws) => ws.workspaceType === WORKSPACE_TYPE_COLLABORATIVE,
  );
  const standardWorkspaces = workspaces.filter(
    (ws) => ws.workspaceType === WORKSPACE_TYPE_STANDARD,
  );

  // Collect all unique user IDs from collaborative and standard workspaces
  const userIdsSet = collectUserIds([
    ...collaborativeWorkspaces,
    ...standardWorkspaces,
  ]);

  // Fetch user images for all members
  const userImages = await fetchUserImages(
    Array.from(userIdsSet),
    getAccessToken,
  );

  const groups: WorkspaceGroup[] = [];

  // Add Private Workspaces group
  const privateGroup = createWorkspaceGroup(
    privateWorkspaces,
    GROUP_TITLE_PRIVATE,
  );
  if (privateGroup) {
    groups.push(privateGroup);
  }

  // Add Collaborative Workspaces group
  const collaborativeGroup = createWorkspaceGroup(
    collaborativeWorkspaces,
    GROUP_TITLE_COLLABORATIVE,
    userImages,
  );
  if (collaborativeGroup) {
    groups.push(collaborativeGroup);
  }

  // Add Standard Workspaces group
  const standardGroup = createWorkspaceGroup(
    standardWorkspaces,
    GROUP_TITLE_STANDARD,
    userImages,
  );
  if (standardGroup) {
    groups.push(standardGroup);
  }

  return groups;
};

/**
 * Transforms and formats all UserWorkspace items.
 * - Fetches user images and user data for all workspace users (private = 1 user, others may have many)
 * - Sorts dataRoom documents and adds counts for easier access
 * - Enhances workspace data with user metadata including online status
 *
 * @param workspaces - Raw workspace data from API
 * @param getAccessToken - Function to get access token for API calls
 * @returns Promise resolving to array of transformed workspaces
 */
export const transformAllUserWorkspaces = async (
  workspaces: UserWorkspaceResponse,
  getAccessToken: AuthContextProps['getAccessToken'],
): Promise<TransformedUserWorkspace[]> => {
  // Collect all unique user IDs from all workspaces (private have 1 user, others may have many)
  const userIdsSet = collectUserIds(workspaces);

  // Fetch user images and user data for all members in parallel
  const [allUserImages, allUserData] = await Promise.all([
    fetchUserImages(Array.from(userIdsSet), getAccessToken),
    fetchUserData(Array.from(userIdsSet), getAccessToken),
  ]);

  // Transform each workspace
  return workspaces.map((workspace) => {
    // Sort dataRoom arrays
    const sortedFiles = sortUnknownArray(
      workspace.dataRoom.workspaceDataRoomFiles,
    );
    const sortedCollections = sortUnknownArray(
      workspace.dataRoom.workspaceDataRoomCollections,
    );
    const sortedStores = sortUnknownArray(
      workspace.dataRoom.workspaceCentralDataStores,
    );

    // Get user images, metadata, and full user data for this workspace
    // Private workspaces have exactly one user; collaborative/standard may have many
    const workspaceUserImages: Record<string, string> = {};
    const workspaceUserMetadata: Record<string, UserMetadata> = {};
    const workspaceUserData: Record<string, PostUserResponse> = {};

    Object.entries(workspace.workspaceUsers).forEach(([userId, isOwner]) => {
      if (allUserImages[userId]) {
        workspaceUserImages[userId] = allUserImages[userId];
        workspaceUserMetadata[userId] = {
          image: allUserImages[userId],
          isOwner: isOwner,
          isOnline: false, // Placeholder - will be updated when API is available
          userId: userId,
        };
      }
      // Store full user data if available
      if (allUserData[userId]) {
        workspaceUserData[userId] = allUserData[userId];
      }
    });

    return {
      ...workspace,
      dataRoom: {
        workspaceDataRoomFiles: {
          items: sortedFiles,
          count: sortedFiles.length,
        },
        workspaceDataRoomCollections: {
          items: sortedCollections,
          count: sortedCollections.length,
        },
        workspaceCentralDataStores: {
          items: sortedStores,
          count: sortedStores.length,
        },
      },
      workspaceUsers: {
        users: workspace.workspaceUsers,
        userImages: workspaceUserImages,
        userMetadata: workspaceUserMetadata,
        userData: workspaceUserData,
      },
    };
  });
};
